import { InvoiceData, InvoiceStatusType, InvoiceType } from 'types/invoices';

type UsedInvoice = {
    isCredit: boolean;
    isDebit: boolean;
    isPrepayment: boolean;
    isSent: boolean;
    isApproved: boolean;
    isFinalized: boolean;
    isCreditable: boolean;
    isCredited: boolean;
    isPartiallyCredited: boolean;
    isCreditFromDebit: boolean;
    isSplittable: boolean;
    isDeletable: boolean;
    isMergeable: boolean;
    isCorrectable: boolean;
    isCorrection: boolean;
    isPaid: boolean;
    isPostponed: boolean;
    /** A copy carrying one payer's share of an invoice split between several payers. */
    isSharedPayment: boolean;
};

export const useInvoice = (invoice: InvoiceData | undefined): UsedInvoice => {
    const isCredit = invoice?.invoiceTypeCode === InvoiceType.CREDIT;
    const isDebit = invoice?.invoiceTypeCode === InvoiceType.DEBIT;
    const isCorrection = invoice?.invoiceTypeCode === InvoiceType.CORRECTION;
    const isPrepayment = invoice?.invoiceTypeCode === InvoiceType.PREPAYMENT;
    const isSent = invoice?.invoiceStatusTypeCode === InvoiceStatusType.SENT;
    const isPostponed = invoice?.invoiceStatusTypeCode === InvoiceStatusType.POSTPONED;
    const isApproved =
        invoice?.invoiceStatusTypeCode !== InvoiceStatusType.DRAFT &&
        invoice?.invoiceStatusTypeCode !== InvoiceStatusType.REVIEWED;
    const isFinalized =
        invoice?.invoiceStatusTypeCode !== InvoiceStatusType.DRAFT &&
        invoice?.invoiceStatusTypeCode !== InvoiceStatusType.REVIEWED &&
        invoice?.invoiceStatusTypeCode !== InvoiceStatusType.APPROVED;
    const isPaid = invoice?.invoiceStatusTypeCode === InvoiceStatusType.PAID;

    const isCreditable =
        (invoice?.invoiceTypeCode === InvoiceType.DEBIT || invoice?.invoiceTypeCode === InvoiceType.PREPAYMENT) &&
        (invoice.invoiceStatusTypeCode === InvoiceStatusType.SENT ||
            invoice.invoiceStatusTypeCode === InvoiceStatusType.PAID);

    const isCredited = invoice?.invoiceStatusTypeCode === InvoiceStatusType.CREDITED;
    const isPartiallyCredited = !!invoice?.correctionInvoice;
    const isCreditFromDebit = isCredit && !!invoice?.debitInvoice?.id;
    const isSharedPayment = invoice?.isSharedPayment ?? false;
    // A shared-payment copy is managed through its group's payment distribution and cannot be copied again.
    const isSplittable =
        invoice?.invoiceTypeCode === InvoiceType.DEBIT &&
        invoice?.invoiceStatusTypeCode === InvoiceStatusType.FINALIZED &&
        !isSharedPayment;

    const isSplitInvoice = invoice?.isSplit ?? false;
    const isMergeable = !isSplitInvoice && isDebit && invoice?.invoiceStatusTypeCode === InvoiceStatusType.FINALIZED;
    const isCorrectable =
        !invoice?.hasCreditInvoice &&
        !isSplitInvoice &&
        (isDebit || isCorrection) &&
        (invoice?.invoiceStatusTypeCode === InvoiceStatusType.SENT ||
            invoice?.invoiceStatusTypeCode === InvoiceStatusType.PAID);
    const isDeletable =
        (!isSplitInvoice &&
            (invoice?.invoiceStatusTypeCode === InvoiceStatusType.DRAFT ||
                invoice?.invoiceStatusTypeCode === InvoiceStatusType.REVIEWED ||
                invoice?.invoiceStatusTypeCode === InvoiceStatusType.APPROVED)) ||
        (isSplitInvoice && invoice?.invoiceStatusTypeCode === InvoiceStatusType.FINALIZED);

    return {
        isCredit,
        isDebit,
        isPrepayment,
        isSent,
        isApproved,
        isFinalized,
        isCreditable,
        isCredited,
        isPartiallyCredited,
        isCreditFromDebit,
        isSplittable,
        isDeletable,
        isMergeable,
        isCorrectable,
        isCorrection,
        isPaid,
        isPostponed,
        isSharedPayment,
    };
};
